import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Video, Upload, Trash2, Play, Loader2, Clock, Camera, Square, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";

/**
 * FounderVideoIntroCard
 * ---------------------
 * Lets the founder upload a ≤60-second MP4/WebM self-recorded pitch that will
 * play on the public passport share page. Client validates duration + size
 * before dispatching.
 */
const MAX_BYTES = 25 * 1024 * 1024;
const MAX_SECONDS = 60;

export default function FounderVideoIntroCard() {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const inputRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const liveVideoRef = useRef(null);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/startup/video-intro");
      setMeta(r.data || null);
    } catch (e) {
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Only stream when meta indicates a video exists.
    if (!meta?.has_video) { setPreviewUrl(null); return; }
    let revoked = null;
    api.get("/startup/video-intro/stream", { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        revoked = url;
        setPreviewUrl(url);
      })
      .catch(() => setPreviewUrl(null));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [meta?.has_video, meta?.uploaded_at, reloadKey]);

  const measureDuration = (file) =>
    new Promise((resolve) => {
      try {
        const url = URL.createObjectURL(file);
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => {
          URL.revokeObjectURL(url);
          resolve(Number.isFinite(v.duration) ? v.duration : null);
        };
        v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        v.src = url;
      } catch {
        resolve(null);
      }
    });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Video too large (max 25 MB).");
      e.target.value = ""; return;
    }
    if (!["video/mp4", "video/webm", "video/quicktime"].includes(file.type)) {
      toast.error("Please upload an MP4 or WebM.");
      e.target.value = ""; return;
    }
    const duration = await measureDuration(file);
    if (duration && duration > MAX_SECONDS + 1) {
      toast.error(`Video is ${duration.toFixed(1)}s — please keep it under 60 seconds.`);
      e.target.value = ""; return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (duration) fd.append("duration_seconds", String(duration));
      await api.post("/startup/video-intro", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Intro video uploaded. It will play on your public passport.");
      setReloadKey((k) => k + 1);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove intro video from your public passport?")) return;
    try {
      await api.delete("/startup/video-intro");
      toast.success("Intro video removed.");
      setMeta(null); setPreviewUrl(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ------------------------- Browser recording -------------------------
  const stopStream = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setRecording(false);
  };

  const openRecorder = () => {
    // Show the consent screen first — no getUserMedia yet.
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Your browser doesn't support in-browser recording. Please upload a file.");
      return;
    }
    setRecordedBlob(null);
    let alreadyConsented = false;
    try { alreadyConsented = sessionStorage.getItem("vz_recorder_consent") === "1"; } catch { /* ignore */ }
    setConsentGiven(alreadyConsented);
    setRecorderOpen(true);
    if (alreadyConsented) {
      // Immediately re-open the camera so retakes flow without another Allow.
      // Wait a tick so the modal mounts and the <video ref> exists.
      setTimeout(() => { acceptConsentAndStartCamera(); }, 0);
    }
  };

  const acceptConsentAndStartCamera = async () => {
    setConsentGiven(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        liveVideoRef.current.muted = true;
        liveVideoRef.current.play().catch(() => { /* ignore */ });
      }
      try { sessionStorage.setItem("vz_recorder_consent", "1"); } catch { /* ignore */ }
    } catch (e) {
      toast.error("Camera / microphone access denied.");
      setRecorderOpen(false);
      setConsentGiven(false);
      try { sessionStorage.removeItem("vz_recorder_consent"); } catch { /* ignore */ }
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
        ? "video/webm;codecs=vp8,opus"
        : "video/webm";
    const rec = new MediaRecorder(streamRef.current, { mimeType: mime });
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setRecordedBlob(blob);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
    recorderRef.current = rec;
    rec.start();
    setRecording(true);
    setCountdown(60);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current); timerRef.current = null;
          try { rec.stop(); } catch { /* ignore */ }
          setRecording(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    setRecording(false);
  };

  const closeRecorder = () => {
    stopStream();
    setRecorderOpen(false);
    setRecordedBlob(null);
    setConsentGiven(false);
    setCountdown(60);
  };

  const uploadRecorded = async () => {
    if (!recordedBlob) return;
    if (recordedBlob.size > MAX_BYTES) {
      toast.error("Recording is larger than 25 MB.");
      return;
    }
    const duration = Math.max(1, 60 - countdown);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([recordedBlob], "founder-intro.webm", { type: "video/webm" }));
      fd.append("duration_seconds", String(duration));
      await api.post("/startup/video-intro", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Intro recorded and saved to your passport.");
      closeRecorder();
      setReloadKey((k) => k + 1);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  useEffect(() => () => stopStream(), []);

  return (
    <div className="glass rounded-lg p-6" data-testid="founder-video-intro-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-md bg-gold/15 border border-gold/40 flex items-center justify-center">
          <Video size={16} className="text-[#8A6600]" />
        </div>
        <div className="flex-1">
          <div className="font-heading text-ink text-lg">Founder Intro Video</div>
          <div className="text-xs text-ink-muted mt-0.5">
            A 60-second self-recorded pitch shown on your public passport. MP4 or WebM. Up to 25 MB.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-ink-muted flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : meta?.has_video ? (
        <div>
          <div className="rounded-md overflow-hidden border border-navy/10 bg-black" style={{ aspectRatio: "16 / 9" }}>
            {previewUrl ? (
              <video
                src={previewUrl}
                controls
                playsInline
                className="w-full h-full object-contain bg-black"
                data-testid="founder-video-intro-preview"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-ink-muted text-xs">
                <Loader2 size={14} className="animate-spin mr-2" /> Loading preview…
              </div>
            )}
          </div>
          <div className="mt-4 flex items-center justify-between text-xs font-mono text-ink-muted">
            <span className="flex items-center gap-1.5">
              <Clock size={12} />
              {meta.duration_seconds ? `${meta.duration_seconds.toFixed(1)}s` : "unknown duration"}
              {meta.size ? ` · ${(meta.size / (1024 * 1024)).toFixed(1)} MB` : ""}
            </span>
            <span>{meta.uploaded_at ? formatDateTime(meta.uploaded_at) : ""}</span>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-widest rounded-md border border-navy/15 text-ink hover:border-gold transition-colors"
              data-testid="founder-video-intro-replace"
            >
              <Upload size={13} /> Replace
            </button>
            <button
              type="button"
              onClick={openRecorder}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-widest rounded-md border border-navy/15 text-ink hover:border-gold transition-colors"
              data-testid="founder-video-intro-record-btn"
            >
              <Camera size={13} /> Record now
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-widest rounded-md border border-rose-500/40 text-rose-700 hover:bg-rose-500/10 transition-colors"
              data-testid="founder-video-intro-delete"
            >
              <Trash2 size={13} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-full rounded-md border border-dashed border-navy/25 hover:border-gold hover:bg-gold/5 transition-colors p-8 text-center"
            data-testid="founder-video-intro-upload"
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-2 text-sm text-ink">
                <Loader2 size={16} className="animate-spin" /> Uploading…
              </div>
            ) : (
              <>
                <Play size={22} className="mx-auto text-[#8A6600] mb-2" />
                <div className="font-heading text-ink text-sm">Upload a 60-second intro</div>
                <div className="text-xs text-ink-muted mt-1">MP4 / WebM · max 25 MB · shown on public passport</div>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={openRecorder}
            disabled={uploading}
            className="inline-flex items-center justify-center gap-2 py-2.5 rounded-md border border-navy text-navy hover:bg-navy hover:text-cream transition-colors text-xs font-heading uppercase tracking-widest"
            data-testid="founder-video-intro-record-empty-btn"
          >
            <Camera size={14} /> Record straight from your camera instead
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        className="hidden"
        onChange={handleFile}
        data-testid="founder-video-intro-file-input"
      />

      {recorderOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-navy/70"
          data-testid="founder-video-recorder-modal"
        >
          <div className="w-full max-w-2xl bg-white rounded-lg p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-ink-muted">RECORD · 60-SECOND INTRO</div>
                <div className="font-heading text-ink text-lg mt-1">Look at the camera. Speak clearly.</div>
              </div>
              <button
                type="button"
                onClick={closeRecorder}
                className="p-1.5 rounded-full hover:bg-navy/[0.06]"
                data-testid="founder-video-recorder-close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="rounded-md overflow-hidden bg-black relative" style={{ aspectRatio: "16 / 9" }}>
              {!consentGiven ? (
                <div
                  className="w-full h-full flex flex-col items-center justify-center px-6 text-center"
                  data-testid="founder-video-recorder-consent"
                >
                  <Camera size={26} className="text-[#C4F751] mb-3" />
                  <div className="text-cream font-heading text-base mb-1.5">
                    Recording will use your camera and microphone.
                  </div>
                  <div className="text-cream/70 text-xs max-w-sm mb-5">
                    We&apos;ll open the browser preview once you continue. Nothing is uploaded until you review the take and hit <em>Save to passport</em>.
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={closeRecorder}
                      className="px-3 py-2 text-[11px] font-heading uppercase tracking-widest rounded-md border border-white/20 text-cream hover:bg-white/[0.06]"
                      data-testid="founder-video-recorder-consent-cancel"
                    >
                      Not now
                    </button>
                    <button
                      type="button"
                      onClick={acceptConsentAndStartCamera}
                      className="px-4 py-2 text-[11px] font-heading uppercase tracking-widest rounded-md bg-[#C4F751] text-[#0B2E24] hover:bg-[#B4E93A]"
                      data-testid="founder-video-recorder-consent-accept"
                    >
                      <Camera size={12} className="inline mr-1.5" /> Allow &amp; open camera
                    </button>
                  </div>
                </div>
              ) : recordedBlob ? (
                <video
                  src={URL.createObjectURL(recordedBlob)}
                  controls
                  playsInline
                  className="w-full h-full object-contain bg-black"
                  data-testid="founder-video-recorder-preview"
                />
              ) : (
                <video
                  ref={liveVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  data-testid="founder-video-recorder-live"
                />
              )}
              {recording && (
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 bg-rose-600 text-white text-[11px] font-mono rounded">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> REC · {countdown}s
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-ink-muted font-mono">
                {!consentGiven
                  ? "Waiting for your consent."
                  : recordedBlob
                    ? `Recorded ${Math.max(1, 60 - countdown)}s · ${(recordedBlob.size / (1024 * 1024)).toFixed(1)} MB`
                    : recording ? "Recording…" : "Ready when you are."}
              </div>
              <div className="flex items-center gap-2">
                {!consentGiven ? null : recordedBlob ? (
                  <>
                    <button
                      type="button"
                      onClick={() => { setRecordedBlob(null); openRecorder(); }}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-widest rounded-md border border-navy/15 text-ink hover:border-gold"
                      data-testid="founder-video-recorder-retake"
                    >
                      <Camera size={13} /> Retake
                    </button>
                    <button
                      type="button"
                      onClick={uploadRecorded}
                      disabled={uploading}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-widest rounded-md bg-navy text-cream hover:bg-[#256E58]"
                      data-testid="founder-video-recorder-save"
                    >
                      {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                      {uploading ? "Uploading…" : "Save to passport"}
                    </button>
                  </>
                ) : recording ? (
                  <button
                    type="button"
                    onClick={stopRecording}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-widest rounded-md bg-rose-600 text-white hover:bg-rose-700"
                    data-testid="founder-video-recorder-stop"
                  >
                    <Square size={13} /> Stop
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRecording}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-widest rounded-md bg-navy text-cream hover:bg-[#256E58]"
                    data-testid="founder-video-recorder-start"
                  >
                    <Video size={13} /> Start recording
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
